import { useQuery } from '@tanstack/react-query';
import { useWallet } from '@txnlab/use-wallet';
import axios from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Calendar, ChevronDown, History, Percent, Trophy, Users } from 'lucide-react';
import { useState } from 'react';
import { getLastLotteryWinner } from '../firebase-client';
import { useAllKitties } from '../hooks/useAllKitties';
import { useCitiesStore } from '../hooks/useCitiesStore';
import { useMyCities } from '../hooks/useMyCities';
import WalletAddress from './WalletAddress';

dayjs.extend(relativeTime);

const Progress = ({ value, className }) => {
  return (
    <div className={clsx('h-2 bg-gray-800 rounded-full overflow-hidden', className)}>
      <div className={clsx('h-full bg-green-500 rounded-full', className)} style={{ width: `${value}%` }}></div>
    </div>
  );
};

const Separator = () => {
  return <div className='h-1 my-4 bg-gray-800'></div>;
};

const TheLotteryPage = () => {
  const { activeAddress } = useWallet();
  const nextSunday = dayjs().startOf('week').add(1, 'week').format('MMM D, YYYY');
  const lastSunday = dayjs().startOf('week').format('MMM D, YYYY');
  const { allKitties } = useAllKitties();
  const { allCities } = useCitiesStore();
  const { myCities } = useMyCities();
  const kittyIds = allKitties.map((kitty) => kitty.id);
  const totalShitizens = allCities.flatMap((city) => city.citizens).filter((citizen) => kittyIds.includes(citizen)).length;
  const myShitizens = myCities.flatMap((city) => city.citizens).filter((citizen) => kittyIds.includes(citizen)).length;

  const vaultTotalQuery = useQuery({
    queryFn: async () => {
      const vaultWallet = 'SKVJU7NP7QMAXYZR744DBIQH67SFSKO7ZD46CSSPFZSMLN4CNLY44B24OA';
      const { data: vaultWalletBalance } = await axios.get(`https://mainnet-idx.algonode.cloud/v2/accounts/${vaultWallet}`);
      const algoBalanceInVaultWallet = vaultWalletBalance.account.amount / 1e6;
      return algoBalanceInVaultWallet;
    },
    queryKey: ['vault-total'],
  });
  const vaultTotal = vaultTotalQuery.data ? Math.floor(vaultTotalQuery.data) : 0;

  const lastWinnerQuery = useQuery({
    queryFn: async () => {
      const lastWinner = await getLastLotteryWinner();
      if (!lastWinner) {
        return null;
      }
      return lastWinner;
    },
    queryKey: ['last-winner'],
  });
  const lastWinner = lastWinnerQuery.data?.owner;

  const prize = 50;
  const target = prize + 50;
  const progressPercentage = typeof vaultTotal === 'number' ? (vaultTotal / target) * 100 : 0;
  const oddsOfWinning = myShitizens ? (myShitizens / totalShitizens) * 100 : 0;
  const isDrawingReady = vaultTotal >= target;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <div className='max-w-xl mx-auto'>
        <div className='bg-gray-900 relative overflow-hidden block w-full rounded-lg border border-gray-800 p-4'>
          <div className='space-y-3'>
            <div className='flex justify-between items-center'>
              <span className='text-gray-400'>Vault Total</span>
              <span className={`text-xl font-bold ${isDrawingReady ? 'text-green-500' : 'text-red-400'}`}>{vaultTotal} $ALGO</span>
            </div>
            <div className='space-y-2'>
              <Progress value={progressPercentage} className={`h-2 bg-gray-800 ${isDrawingReady ? '[&>div]:bg-green-500' : '[&>div]:bg-red-400'}`} />
              <div className='flex justify-between text-xs text-gray-500'>
                <span>0 $ALGO</span>
                <span>Minimum: {target} $ALGO</span>
              </div>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-gray-400'>Prize</span>
              <div className='flex items-center gap-2'>
                <Trophy className='w-4 h-4 text-yellow-500' />
                <span className='text-xl font-bold text-yellow-500'>{prize} $ALGO</span>
              </div>
            </div>
          </div>

          <Separator />

          <div className='space-y-3'>
            <div className='flex justify-between items-center'>
              <div className='flex items-center gap-2'>
                <Calendar className='w-4 h-4 text-blue-500' />
                <span className='text-gray-400'>Next drawing</span>
              </div>
              <div className='flex items-center gap-2'>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${isDrawingReady ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}
                >
                  {isDrawingReady ? 'READY' : 'PENDING'}
                </span>
                <span className='font-medium'>{nextSunday}</span>
              </div>
            </div>
            <div className='flex justify-between items-center'>
              <div className='flex items-center gap-2'>
                <History className='w-4 h-4 text-purple-500' />
                <span className='text-gray-400'>Last drawing</span>
              </div>
              <span className='font-medium'>{lastSunday}</span>
            </div>
          </div>

          <Separator />

          {/* Statistics */}
          <div className='space-y-4'>
            <div className='flex justify-between items-center'>
              <div className='flex items-center gap-2'>
                <Users className='w-4 h-4 text-indigo-500' />
                <span className='text-gray-400'>Total Shitty Kitty Shitizens</span>
              </div>
              <span className='font-medium'>{totalShitizens.toLocaleString()}</span>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between items-center'>
                <span className='text-gray-400'>My Shitizens</span>
                <span className='font-medium'>{myShitizens}</span>
              </div>
              <Progress value={(myShitizens / 100) * 100} className='h-2 bg-gray-800 [&>div]:bg-secondary-500' />
              <div className='flex justify-between text-xs text-gray-500'>
                <span>0 Shitizens</span>
                <span>100+ Shitizens</span>
              </div>
            </div>
            <div className='flex justify-between items-center'>
              <div className='flex items-center gap-2'>
                <Percent className='w-4 h-4 text-emerald-500' />
                <span className='text-gray-400'>Odds of winning</span>
              </div>
              <span className='font-medium'>{oddsOfWinning.toFixed(2)}%</span>
            </div>
          </div>

          <Separator />

          {lastWinner && (
            <div className='space-y-2'>
              <div className='flex justify-between items-center'>
                <span className='text-gray-400 text-sm'>Last winner</span>
                <span className='text-gray-400 text-sm'>{lastSunday}</span>
              </div>

              <div className='p-3 bg-gray-800 rounded-lg break-all'>
                <WalletAddress address={lastWinner} />
              </div>
            </div>
          )}

          <Separator />

          <div>
            <div>
              <button type='button' className='flex items-center justify-between w-full py-3' onClick={() => setIsOpen(!isOpen)}>
                <span className='text-sm font-medium'>How it works</span>
                <ChevronDown className='h-4 w-4 text-gray-400' />
              </button>
            </div>

            <div className={clsx({ hidden: !isOpen }, 'prose prose-p:text-neutral-400')}>
              <p>The Shitty Kitty Lottery is a weekly draw where participants can win $ALGO based on their Shitizen count.</p>
              <h4 className='font-medium text-white mt-2'>Increasing Your Odds</h4>
              <p>
                Your chances of winning increase with each Shitty Kitty Shitizen you own. The more Shitizens in your cities, the higher your odds!
              </p>
              <h4 className='font-medium text-white mt-2'>How the Lottery Works</h4>
              <p>
                Every Sunday, if the Vault Total reaches at least 100 $ALGO, a drawing takes place. Your odds of winning are directly proportional to
                the number of Shitizens you own compared to the total number of Shitizens.
              </p>
              <h4 className='font-medium text-white mt-2'>Contributing to the Vault</h4>
              <p>The Vault Total grows through various activities on the platform:</p>
              <ul className='list-disc list-inside space-y-1 ml-2'>
                <li>Purchasing/selling Shitty assets on the site</li>
                <li>Using the Trait Swapper</li>
                <li>Upgrading your Shitty City</li>
                <li>Participating in future utilities and features</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheLotteryPage;
